import { Link } from "react-router-dom";
import { ArrowRight, FileText, CalendarDays } from "lucide-react";
import { Eyebrow, Reveal, MaskLineView, ArrowCta } from "../components/ui-bits";
import JsonLd from "../components/JsonLd";
import AnalystClosing from "../components/AnalystClosing";
import { ARTICLES, CONTENT, ARTICLE_META } from "../content/articles";
import { useLang } from "../i18n/LangContext";

const SITE = "https://verdecapital.eu";

const T = {
  en: {
    eyebrow: "Verde Insights",
    h1: "Development finance, explained.",
    copy: "Educational resources on financing Portuguese residential development — the stages, the security, the exits and the market. Written by the Verde Capital credit team and clearly separated from our commercial services.",
    readGuide: "Read the guide",
    minRead: "min read",
    featured: "Featured guide",
    more: "More reading",
    plannedTitle: "Planned topics",
    planned: [
      "What senior-secured lending means",
      "How loan-to-value ratios work in development finance",
      "How lenders assess land with development potential",
      "Common reasons development finance applications are declined",
      "How to prepare a development finance application",
      "What documents lenders require",
      "The role of independent valuations",
      "How real estate private credit works",
    ],
    cta: "Get indicative terms",
  },
  pt: {
    eyebrow: "Verde Insights",
    h1: "Financiamento ao desenvolvimento, explicado.",
    copy: "Recursos educativos sobre o financiamento do desenvolvimento residencial em Portugal — as fases, as garantias, as saídas e o mercado. Escritos pela equipa de crédito da Verde Capital e claramente separados dos nossos serviços comerciais.",
    readGuide: "Ler o guia",
    minRead: "min de leitura",
    featured: "Guia em destaque",
    more: "Mais leituras",
    plannedTitle: "Temas planeados",
    planned: [
      "O que significa crédito com garantia sénior",
      "Como funcionam os rácios empréstimo-valor",
      "Como os credores avaliam terrenos com potencial",
      "Razões comuns para recusa de candidaturas",
      "Como preparar uma candidatura de financiamento",
      "Que documentos os credores exigem",
      "O papel das avaliações independentes",
      "Como funciona o crédito privado imobiliário",
    ],
    cta: "Obter condições indicativas",
  },
  nl: {
    eyebrow: "Verde Inzichten",
    h1: "Ontwikkelfinanciering, uitgelegd.",
    copy: "Educatief materiaal over financiering van residentiële ontwikkeling in Portugal — de fasen, de zekerheden, de exits en de markt. Geschreven door het kredietteam van Verde Capital en duidelijk gescheiden van onze commerciële diensten.",
    readGuide: "Lees de gids",
    minRead: "min leestijd",
    featured: "Uitgelichte gids",
    more: "Meer leesvoer",
    plannedTitle: "Geplande onderwerpen",
    planned: [
      "Wat senior gedekt krediet betekent",
      "Hoe loan-to-value-ratio's werken",
      "Hoe kredietverstrekkers grond met potentieel beoordelen",
      "Veelvoorkomende redenen om aanvragen af te wijzen",
      "Hoe u een financieringsaanvraag voorbereidt",
      "Welke documenten kredietverstrekkers eisen",
      "De rol van onafhankelijke taxaties",
      "Hoe privaat vastgoedkrediet werkt",
    ],
    cta: "Indicatieve voorwaarden aanvragen",
  },
};

export default function Insights() {
  const { lang, lp } = useLang();
  const t = T[lang];
  const guide = CONTENT["development-finance-portugal-guide"][lang] || CONTENT["development-finance-portugal-guide"].en;
  const articles = ARTICLES.filter((a) => a !== "development-finance-portugal-guide").map((slug) => ({ slug, c: CONTENT[slug][lang] || CONTENT[slug].en }));

  return (
    <div className="bg-cream">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: t.h1,
        url: `${SITE}${lp("/insights")}`,
        publisher: { "@type": "Organization", name: "Verde Capital" },
      }} />

      <section className="pt-32 sm:pt-40 pb-12">
        <div className="container-x max-w-3xl">
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.4rem] sm:text-5xl text-charcoal" data-testid="insights-headline">
            <MaskLineView>{t.h1}</MaskLineView>
          </h1>
          <Reveal delay={0.2} className="mt-6">
            <p className="text-lg leading-relaxed text-ink">{t.copy}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-14">
        <div className="container-x">
          <Reveal>
            <img
              src="/insights-header.jpg"
              alt="Architect's desk with rolled drawings, a notebook and reading glasses overlooking the terracotta rooftops of Lisbon at sunrise"
              className="aspect-[16/9] w-full rounded-3xl border border-sand object-cover shadow-[0_30px_60px_-30px_rgba(7,39,29,0.35)] sm:aspect-[16/7]"
              loading="eager"
              decoding="async"
              data-testid="insights-header-image"
            />
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-x">
          {/* Featured guide */}
          <Reveal>
            <Link
              to={lp("/insights/development-finance-portugal-guide")}
              data-testid="insights-guide-card"
              className="group block rounded-2xl bg-verde-950 p-8 sm:p-12 text-cream grain transition-shadow duration-300 hover:shadow-2xl hover:shadow-verde-950/30"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mint">{t.featured}</p>
              <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold text-white max-w-2xl">{guide.h1}</h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-cream/75">{guide.standfirst}</p>
              <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-mint transition-transform duration-300 group-hover:translate-x-1">
                {t.readGuide} <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </Reveal>

          {/* Articles */}
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={0.08 + i * 0.07}>
                <Link
                  to={lp(`/insights/${a.slug}`)}
                  data-testid={`insights-article-${i}`}
                  className="card flex h-full flex-col p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-verde-950/10"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-verde-50 text-verde-700">
                    <FileText className="h-4.5 w-4.5" strokeWidth={1.7} />
                  </span>
                  <h2 className="mt-4 font-display text-lg font-bold leading-snug text-charcoal">{a.c.h1}</h2>
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-fog">{a.c.standfirst}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-verde-800">
                    {t.readGuide} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Planned topics */}
      <section className="pb-16">
        <div className="container-x max-w-3xl">
          <Reveal>
            <div className="rounded-2xl border border-sand bg-white p-8" data-testid="insights-planned">
              <h2 className="font-display text-xl font-bold text-charcoal">{t.plannedTitle}</h2>
              <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                {t.planned.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-[13px] text-ink">
                    <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brass" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 text-center">
            <ArrowCta to={lp("/apply")}>{t.cta}</ArrowCta>
          </Reveal>
        </div>
      </section>

      <AnalystClosing />
    </div>
  );
}
